import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** CRM contact search can hang (e.g. unsupported remote locale); fall back to invitee fields. */
const CONTACT_SEARCH_TIMEOUT_MS = 8000

/**
 * Normalizes a contact from RemoteContactsSearchHandler (legacy dashboard vs CRM MFE can return different shapes).
 * - Identifiers: legacy uses uuid; CRM may use id and/or uuid.
 * - Name: legacy uses name; CRM may use fullName.
 * - Timezone: dashboard uses timeZone (camelCase) or time_zone (snake_case).
 */
const normalizeContact = (contact) => {
  if (!contact) return null
  return {
    id: contact.id ?? contact.uuid,
    uuid: contact.uuid ?? contact.id,
    name: contact.name ?? contact.fullName,
    email: contact.email,
    phone: contact.phone ?? contact.phoneNumber ?? contact.phone_number,
    timeZone: contact.timeZone ?? contact.time_zone,
    avatarUrl: contact.avatarUrl,
  }
}

/**
 * Resolves attendee details by prioritizing contact data over invitee data.
 * Uses RemoteContactsSearchHandler results to merge contact-first with invitee-fallback.
 *
 * Contact shape: handler may return legacy (uuid, name, timeZone) or CRM (id, fullName, etc.).
 * We normalize so resolved.contactId works for routing (/app/contacts/:id) and
 * resolved.contactUuid is the value to send to APIs (e.g. send_from_calendly expects contactUuid).
 *
 * The handler intentionally avoids useCallback to match how CrmInvitee
 * consumes RemoteContactsSearchHandler — the unstable reference allows the
 * MFE's internal revalidation cycle to push updated data (e.g. a newly
 * uploaded avatar) through the callback on subsequent renders.
 *
 * @param {Object} invitee - The invitee object from the event
 * @param {{ nameSource?: 'contact' | 'signup' }} [options]
 * @returns {{
 *   resolved: Object|null, contact: Object|null, isLoading: boolean,
 *   handleContactResults: Function, handleContactFetching: Function
 * }}
 */
const inviteeIdentityKey = (invitee) => `${invitee?.uuid ?? ''}|${invitee?.id ?? ''}|${invitee?.email ?? ''}`

export const useResolvedAttendeeDetails = (invitee, { nameSource = 'contact' } = {}) => {
  const [contact, setContact] = useState(null)
  const [isLoading, setIsLoading] = useState(() => Boolean(invitee?.email))
  const prevInviteeKeyRef = useRef(null)
  const inviteeKey = inviteeIdentityKey(invitee)

  useEffect(() => {
    if (prevInviteeKeyRef.current === null) {
      prevInviteeKeyRef.current = inviteeKey
      return
    }
    if (prevInviteeKeyRef.current === inviteeKey) return
    prevInviteeKeyRef.current = inviteeKey
    setContact(null)
    setIsLoading(Boolean(invitee?.email))
  }, [inviteeKey, invitee?.email])

  // Remote contact search can suspend or hang without calling onResults/onFetching
  // (e.g. Pseudo locale on the CRM MFE). Unblock the card with invitee fields.
  useEffect(() => {
    if (!isLoading) return undefined
    const timer = window.setTimeout(() => {
      setIsLoading(false)
    }, CONTACT_SEARCH_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [isLoading, inviteeKey])

  const handleContactResults = (results) => {
    const result = results?.[0] || null
    setContact(result)
    setIsLoading(false)
  }

  const handleContactFetching = useCallback((fetching) => {
    if (!fetching) {
      setIsLoading(false)
    }
  }, [])

  const normalized = useMemo(() => normalizeContact(contact), [contact])

  const resolved = useMemo(
    () => (isLoading ? null : {
      name: nameSource === 'signup'
        ? (invitee.name || normalized?.name || invitee.email)
        : (normalized?.name || invitee.name || invitee.email),
      email: normalized?.email || invitee.email,
      phone: normalized?.phone || invitee.phoneNumber || null,
      timezone: normalized?.timeZone || invitee.standardTimezoneTitle || invitee.timezone || null,
      avatarUrl: normalized?.avatarUrl || null,
      /** For routing (e.g. /app/contacts/:id). Supports both legacy uuid and CRM id. */
      contactId: normalized?.id ?? null,
      /** For API calls (e.g. send_from_calendly contactUuid). Use uuid when present so request is accepted. */
      contactUuid: normalized?.uuid ?? null,
    }),
    [isLoading, nameSource, normalized, invitee.name, invitee.email, invitee.phoneNumber, invitee.standardTimezoneTitle, invitee.timezone]
  )

  return { resolved, contact, isLoading, handleContactResults, handleContactFetching }
}
